import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, Plus, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Sites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null=create, object=edit
  const [showDlg, setShowDlg] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', contact: '', notes: '' });

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/sites`);
      setSites(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sites');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', code: '', contact: '', notes: '' });
    setShowDlg(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name || '', code: s.code || '', contact: s.contact || '', notes: s.notes || '' });
    setShowDlg(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      if (editing) {
        await axios.put(`${API_URL}/sites/${editing.id}`, form);
        toast.success('Site updated');
      } else {
        await axios.post(`${API_URL}/sites`, form);
        toast.success('Site created');
      }
      setShowDlg(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete site "${s.name}"? (Drivers allocated to it will keep their record but lose the link.)`)) return;
    try {
      await axios.delete(`${API_URL}/sites/${s.id}`);
      toast.success('Site deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };
  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API_URL}/sites/seed-defaults`);
      toast.success(`Seeded ${res.data.inserted} default site(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seed failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="sites-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><MapPin className="h-7 w-7" /> Sites</h1>
            <p className="text-muted-foreground text-sm">Manage the contracting sites you allocate owner-drivers to (e.g. Intertown Transport, DPD Laser).</p>
          </div>
          <div className="flex gap-2">
            {sites.length === 0 && (
              <Button variant="outline" onClick={seedDefaults} data-testid="seed-sites-btn">Seed defaults</Button>
            )}
            <Button onClick={openCreate} data-testid="add-site-btn"><Plus className="h-4 w-4 mr-1" />Add Site</Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">All Sites ({sites.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center text-muted-foreground py-6">Loading…</div>
            ) : sites.length === 0 ? (
              <div className="text-center text-muted-foreground py-6">No sites yet. Click <b>Seed defaults</b> or <b>Add Site</b>.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sites.map(s => (
                    <TableRow key={s.id} data-testid={`site-row-${s.id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.code || '—'}</TableCell>
                      <TableCell className="text-sm">{s.contact || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{s.notes || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(s)} data-testid={`edit-site-${s.id}`}><Pencil className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(s)} data-testid={`delete-site-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDlg} onOpenChange={setShowDlg}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Site' : 'New Site'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Intertown Transport" data-testid="site-name-input" />
              </div>
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="INTERTOWN" />
              </div>
              <div>
                <Label>Contact</Label>
                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Phone / email of site contact" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDlg(false)}>Cancel</Button>
                <Button onClick={save} data-testid="save-site-btn">{editing ? 'Save' : 'Create'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
